import React, { useMemo, useState, useCallback } from "react";
import { AutoComplete, Card, Col, Form, Input, Row, Checkbox, Select } from "antd";
import { connect } from "dva";
import { debounce } from "lodash";
import { SecurePassAutoSend, SecurePassAutoSendMap, ShipmentStatus, LocationType } from "@/utils/enum";
import { Validator } from "@/utils/validator";
import AddressBookManager from "@/components/AddressBookManager";
import useAddresses from "@/components/AddressBookManager/useAddresses";
import AddressInput from "@/components/AddressInput";
import PhoneNumberInput from "@/components/PhoneNumberInput";
import ShipmentRestrictedDaysFormItem from "@/components/ShipmentRestrictedDaysFormItem";

const { TextArea } = Input;

const cardStyle = {
  headStyle: {
    color: "white",
    backgroundColor: "#22316C",
  },
};

const LocationInformationInputs = ({
  formRef,
  disabled: disabledProp,
  loading,
  shipmentStatus,
  locationType,
  canSaveAddress,
  configLang,
  dealerId,
  isAdmin,
  securePass,
  onSelectAddress,
  configPerm,
}) => {
  const [isSaveAddressDisabled, setIsSaveAddressDisabled] = useState(false);
  const [searchText, setSearchText] = useState("");

  const disabled = useMemo(() => {
    return disabledProp || shipmentStatus >= ShipmentStatus.Delivered;
  }, [disabledProp, shipmentStatus]);

  const { addresses } = useAddresses({
    dealerId,
    searchText,
    disabled,
    configPerm,
  });

  const showAddressBook = useMemo(() => {
    const hasShipmentStatus = typeof shipmentStatus === "number";
    const beforeThanCompleted = (hasShipmentStatus && shipmentStatus <= ShipmentStatus.Completed) || !hasShipmentStatus;

    return !disabled && !loading && beforeThanCompleted && configPerm["CoreService.Addresses"];
  }, [disabled, loading, shipmentStatus, configPerm]);

  const addressBookDisabled = useMemo(() => {
    if (isAdmin) {
      return dealerId === null;
    }

    return false;
  }, [isAdmin, dealerId]);

  const handleSelectAddress = useCallback(
    (_, option) => {
      const address = addresses.find(address => address.id === option.key);

      if (!address) {
        return;
      }

      const {
        id,
        name: locationName,
        addressLine1,
        addressLine2,
        city,
        stateId,
        stateName,
        stateShortName,
        zipCode,
        contactEmail,
        contactName,
        contactPhone,
        contactPhoneExtension,
        dayOfWeekRestricted,
        pickupNotes,
        dropOffNotes,
        autosendType,
      } = address;

      // when autosendType is not set we get 0 from BE, so we need to set the default value
      const autosendTypeValue = autosendType === 0 ? SecurePassAutoSend.DEFAULT : autosendType;

      // callback copy of the selected address
      typeof onSelectAddress === "function" && onSelectAddress(address);

      formRef.current.setFieldsValue({
        [locationType]: {
          ...formRef.current[locationType],
          originalAddressId: id,
          locationName,
          addressLine1,
          addressLine2,
          city,
          stateId,
          stateName,
          stateShortName,
          zipCode,
          contactName,
          contactPhone,
          contactPhoneExtension,
          instructionsOrRestrictions: locationType === LocationType.Pickup ? pickupNotes : dropOffNotes,
          dayOfWeekRestricted: dayOfWeekRestricted?.split(",") ?? [],
          ...(locationType === LocationType.Pickup && {
            contactEmail,
            autosendType: autosendTypeValue,
          }),
        },
      });
    },
    [addresses, locationType, formRef, onSelectAddress],
  );

  return (
    <Card
      className="noLeftMarginCard"
      type="inner"
      title={`${locationType} ${configLang.CoreService?.Information}`}
      {...cardStyle}
    >
      {showAddressBook && (
        <AddressBookManager
          formRef={formRef}
          disabled={addressBookDisabled}
          locationType={locationType}
          onSelectAddress={onSelectAddress}
        />
      )}

      <Row gutter={16}>
        <Col flex={1}>
          <Form.Item
            name={[locationType, "locationName"]}
            label={`${locationType} ${configLang.CoreService?.LocationName}`}
            rules={[{ required: true }]}
          >
            <AutoComplete
              options={addresses.map(address => ({
                value: address.name,
                key: address.id,
              }))}
              onSearch={debounce(setSearchText, 500)}
              onSelect={handleSelectAddress}
              disabled={disabled}
              placeholder={configLang.CoreService?.LocationNamePlaceholder}
              filterOption={(input, option) => option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24}>
          <AddressInput
            disabled={disabled || shipmentStatus >= ShipmentStatus.Posted}
            formReference={formRef}
            locationType={locationType}
            addressLine1FormItemProps={{
              name: [locationType, "addressLine1"],
            }}
            addressLine2FormItemProps={{
              name: [locationType, "addressLine2"],
            }}
            stateIdFormItemProps={{
              name: [locationType, "stateId"],
            }}
            stateNameFormItemProps={{
              name: [locationType, "stateName"],
            }}
            stateShortNameFormItemProps={{
              name: [locationType, "stateShortName"],
            }}
            zipCodeFormItemProps={{
              name: [locationType, "zipCode"],
            }}
            cityFormItemProps={{
              name: [locationType, "city"],
            }}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={12}>
          <Form.Item
            name={[locationType, "contactName"]}
            label={configLang.CoreService?.ContactName}
            rules={[{ required: true }]}
          >
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={12}>
          <PhoneNumberInput
            disabled={disabled}
            locationType={locationType}
            formItemProps={{
              name: "contactPhone",
              label: configLang.CoreService?.ContactPhone,
              formRef,
              rules: [{ required: true }],
            }}
          />
        </Col>
      </Row>

      {locationType === LocationType.Pickup && (
        <Row gutter={16}>
          <Col flex={1}>
            <Form.Item
              name={[locationType, "contactEmail"]}
              label="Contact Email"
              rules={[Validator.optional(Validator.email, "Please enter a valid email")]}
            >
              <Input disabled={disabled} />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row gutter={16}>
        <Col flex={1}>
          <Form.Item
            name={[locationType, "instructionsOrRestrictions"]}
            label={`${locationType} ${configLang.CoreService?.InstructionsRestrictions}`}
          >
            <TextArea disabled={disabled} rows={4} />
          </Form.Item>
        </Col>
      </Row>

      {securePass?.enabled && locationType === LocationType.Pickup && (
        <Row gutter={16}>
          <Col flex={1}>
            <Form.Item
              name={[locationType, "autosendType"]}
              label="SecurePass Autosend"
              rules={[Validator.required("Please select a secure pass type")]}
              initialValue={SecurePassAutoSend.DEFAULT}
              normalize={value => {
                if (value === 0 || value === undefined || value === null) {
                  return SecurePassAutoSend.DEFAULT;
                }
                return value;
              }}
            >
              <Select disabled={disabled}>
                {Object.values(SecurePassAutoSend).map(type => (
                  <Select.Option key={type} value={type}>
                    {SecurePassAutoSendMap[type]}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      )}

      {canSaveAddress && (
        <Row gutter={16}>
          <Col lg={24}>
            <Form.Item
              name={[locationType, "saveAddress"]}
              valuePropName="checked"
              shouldUpdate={(_, curValues) => {
                if (curValues?.[locationType]?.originalAddressId != null) {
                  setIsSaveAddressDisabled(true);
                } else {
                  setIsSaveAddressDisabled(false);
                }

                return true;
              }}
            >
              <Checkbox disabled={isSaveAddressDisabled}>
                {configLang.CoreService?.Save} {locationType} {configLang.CoreService?.Address}
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row gutter={16}>
        <Col flex={1}>
          <ShipmentRestrictedDaysFormItem disabled={disabled} name={[locationType, "dayOfWeekRestricted"]} />
        </Col>
      </Row>
      <Form.Item name={[locationType, "addressId"]} hidden />
      <Form.Item name={[locationType, "originalAddressId"]} hidden />
    </Card>
  );
};

export default connect(({ configs }) => ({
  configLang: configs.configLang,
  securePass: configs.securePass,
  configPerm: configs.configPerm,
}))(LocationInformationInputs);
